import { trackEvent } from '@helpers/analytics';
import {
  Button,
  ButtonTheme,
  CheckMarkFillSmallIcon,
  CheckMarkIcon,
  NewWindowIcon,
  PackageIcon,
  Typography,
  TypographyType,
} from '@vivino/js-react-common-ui';
import { useI18N } from '@vivino/js-web-common';
import React from 'react';

import styles from './externalBuySection.module.scss';

const TRANSLATIONS_PATH = 'components.shared.external_buy_button';

const TRANSLATIONS: { [key: string]: string } = {
  buttonText: `${TRANSLATIONS_PATH}.button_text`,
};

const FALLBACK_MERCHANT_LOGO_URL = '/images/checkout/default_merchant_logo.png';

export interface ExternalBuySectionProps {
  merchantId: number;
  merchantName: string;
  icon: string;
  disclaimer: string;
  deliveryInformation: string;
  link: string;
  vintageId: number;
  wineryId: number;
  priceAmount: number | string;
  currency: string;
  sponsored: boolean;
}

export default function ExternalBuySection({
  merchantId,
  merchantName,
  icon,
  disclaimer,
  deliveryInformation,
  link,
  vintageId,
  wineryId,
  priceAmount,
  currency,
  sponsored,
}: ExternalBuySectionProps) {
  const { t } = useI18N();
  const iconUrl = icon || FALLBACK_MERCHANT_LOGO_URL;

  const onButtonClick = () => {
    trackEvent({
      event: 'Web Wine Page - Linkout - Click',
      props: {
        linkoutUrl: link,
        priceTypeAmount: 'linkout',
        vintageId,
        wineryId,
        priceAmount,
        currency,
        sponsored,
        merchantId,
      },
    });
  };

  return (
    <>
      <Button
        target="_blank"
        fullWidth
        theme={ButtonTheme.Accent}
        startIcon={<NewWindowIcon />}
        href={link}
        onClick={onButtonClick}
      >
        {t(TRANSLATIONS.buttonText, { merchant_name: merchantName })}
      </Button>
      <div className={styles.disclaimerContainer}>
        <div className={styles.iconsWrapper}>
          <div className={styles.iconContainer}>
            <img
              alt="merchant"
              data-testid="merchant-logo"
              className={styles.merchantIcon}
              src={iconUrl}
            />
          </div>
          <CheckMarkFillSmallIcon className={styles.checkmarkIcon} />
        </div>
        <div>
          <Typography type={TypographyType.BodySmall}>{disclaimer}</Typography>
        </div>
      </div>
      {deliveryInformation && (
        <div className={styles.pickupInformation}>
          <div className={styles.iconsWrapper}>
            <PackageIcon className={styles.merchantIcon} />
            <CheckMarkIcon className={styles.checkmarkIcon} />
          </div>
          <Typography type={TypographyType.BodySmall}>{deliveryInformation}</Typography>
        </div>
      )}
    </>
  );
}
